/**
 * ConclusionCallout — synthesis paragraph for the Conclusion section.
 *
 * Renders the BTM decision summary plus top strength, risk, conditions,
 * and next actions as a single prose block. Verdict labels live on the
 * cover; this callout is body content only.
 */
import React from 'react';
import type { SiteRagReport } from '../../types/siteRag';
import { getDisplayVerdict, tierLabel, getVerdictCapAnnotation } from '../../lib/reportVerdict';
import { resolveEffectiveConclusion } from '../../lib/resolveEffectiveConclusion';
import { useEffectiveReportTheme } from '../../lib/useEffectiveReportTheme';
import { roleColor } from '../../lib/reportRoleColors';
import { ConclusionCategoryStatusTable } from './ConclusionCategoryStatusTable';

interface Props {
  report: SiteRagReport;
  /**
   * De-dup guard — when the PARENT already surfaces the evidence-tier cap
   * sentence (ReferencesSection's Conclusion verdict band renders the SAME
   * `getVerdictCapAnnotation` string as its verdict rationale), suppress this
   * callout's own cap annotation so the cap sentence is not repeated verbatim
   * inside the Conclusion & Verdict section. Default false → standalone callers
   * (and the unit tests) keep the self-contained cap annotation.
   */
  suppressCapAnnotation?: boolean;
}

type RagTone = 'green' | 'amber' | 'red' | 'neutral';

function ragTone(rec?: string): RagTone {
  if (rec === 'Proceed') return 'green';
  if (rec === 'Proceed with Conditions') return 'amber';
  // P1.4 — a pending decision is neutral, never a red: the dossier used to
  // land here with "Do Not Proceed" for a withheld rollup.
  if (rec === 'Decision Pending') return 'neutral';
  return 'red';
}

function resolveBtmDecisionSummary(report: SiteRagReport): string | undefined {
  const fromConclusion = report.conclusion?.btmDecisionSummary?.trim();
  if (fromConclusion) return fromConclusion;
  const topLevel = report.btmDecisionSummary?.trim();
  return topLevel || undefined;
}

/**
 * Append a single terminal period to a field value WITHOUT producing a
 * doubled period. Source fields (key-finding titles, btm summaries,
 * conditions/actions text) frequently already end in `.`/`!`/`?`/`;`/`:`,
 * so a naive `${field}.` template produced run-on artifacts like
 * "…are strong.. Top risk:" / "…before LOI.. Next actions:". This trims
 * any trailing sentence-terminal punctuation first, then adds exactly one
 * period. (Bug-fix Wave A.)
 */
function withSinglePeriod(field: string): string {
  const trimmed = field.replace(/[.;:!?\s]+$/u, '');
  return trimmed.length > 0 ? `${trimmed}.` : '';
}

/**
 * Build the body paragraph from the report's deterministic
 * conclusion fields.
 *
 * Bug-fix Wave A — this callout used to ALSO dump Top strength / Top risk
 * / Key conditions / Next actions, which the ReferencesSection conclusion
 * grid (Top strengths / Key risks / Conditions for proceeding /
 * Recommended next actions, rendered immediately below this callout)
 * already shows as clean structured cards. Repeating them here produced a
 * run-on duplicate paragraph AND, via the `${field}.` joins, doubled
 * periods. The structured cards are now the single source for those four
 * fields; this callout carries only the BTM decision synthesis (which the
 * cards do NOT show) plus a generic fallback when nothing is populated.
 */
function buildBody(report: SiteRagReport, _conc: NonNullable<SiteRagReport['conclusion']>): string {
  const btmSummary = resolveBtmDecisionSummary(report);
  if (btmSummary) return `BTM decision: ${withSinglePeriod(btmSummary)}`;
  return 'Review the structured strengths, risks, conditions, and next actions below, alongside the body sections and risk matrix, for the detailed analysis.';
}

export function ConclusionCallout({ report, suppressCapAnnotation = false }: Props) {
  // Theme-aware chrome: read the effective report theme so every color
  // routes through roleColor() and follows the active preset (was
  // hard-coded amber/gold/green Tailwind semantic classes). The hook
  // resolves to the ThemeOverride context when inside Template Studio's
  // preview, else the store theme, else DEFAULT_THEME — so the test
  // callers (no provider) get a consistent default palette.
  const theme = useEffectiveReportTheme();
  const conclusion = resolveEffectiveConclusion(report);
  if (!conclusion) return null;

  const reportVerdict = getDisplayVerdict(report);
  const capAnnotation = getVerdictCapAnnotation(report, reportVerdict);
  const body = buildBody(report, conclusion);
  const reportType = report.reportType ?? 'feasibility';
  const rag = ragTone(conclusion.recommendation);

  return (
    <>
    <div
      // William QA 2026-06-28 — decorative left rule removed report-wide; the
      // recommendation is surfaced via the `data-rag` hook + the structured
      // strengths/risks block below.
      className="py-1 mb-6"
      data-testid="conclusion-callout"
      data-rag={rag}
      role="region"
      aria-label="Conclusion summary"
    >
      <p className="text-sm leading-relaxed" style={{ color: roleColor(theme, 'ink') }} data-testid="conclusion-callout-body">
        {body}
      </p>

      {/* SF6 — Why-this-tier expandable surfaces the LLM lean drivers
          when the narrative adjusted the deterministic tier. Hidden
          otherwise. */}
      {reportVerdict.source === 'llm-leaned' && reportVerdict.lean && reportVerdict.lean.delta !== 0 && (
        <details className="mt-3 text-[10px]" data-testid="conclusion-callout-why-tier">
          <summary style={{ cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.1em', color: roleColor(theme, 'muted') }}>
            Why {reportVerdict.label}? <span style={{ color: roleColor(theme, 'faint') }}>(adjusted by narrative analysis)</span>
          </summary>
          <ul className="mt-2 space-y-1" style={{ color: roleColor(theme, 'body') }} data-testid="conclusion-callout-why-drivers">
            {reportVerdict.lean.drivers.map(d => (
              <li key={d}>• {d}</li>
            ))}
          </ul>
          {reportVerdict.deterministicTier && reportVerdict.deterministicTier !== 'none' && (
            <div className="mt-2" style={{ color: roleColor(theme, 'muted') }}>
              Deterministic provider data alone scores this site{' '}
              <span className="font-bold" style={{ color: roleColor(theme, 'ink') }}>
                {tierLabel(reportVerdict.deterministicTier, reportType, 'overall')}
              </span>
              ; narrative review adjusts {reportVerdict.lean.delta > 0 ? 'upward' : 'downward'} one tier based on the above.
            </div>
          )}
        </details>
      )}

      {/* EA8 — tier-cap annotation. Visible when a Proceed verdict was
          downgraded to Conditional because the evidence profile didn't
          meet the Tier-1 floor. This IS a warning-toned annotation →
          roleColor(theme, 'warning'). */}
      {capAnnotation && !suppressCapAnnotation && (
        <p
          className="text-xs italic mt-2"
          style={{ color: roleColor(theme, 'warning') }}
          data-testid="conclusion-callout-tier-cap"
        >
          {capAnnotation}
        </p>
      )}
    </div>
    {/* GC-W4 — the golden "Category statuses, evidence-justified" table,
        full-width below the synthesis callout (not indented under its rule).
        Collapses to null on an unscored report. */}
    <ConclusionCategoryStatusTable report={report} />
    </>
  );
}
